import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A British Mark-style RHOMBOID landship of the Great War. The signature read
// is the lozenge track outline: each side is a closed ring of six angled track
// boxes tracing the rhomboid profile (high front horn climbing over the nose,
// long tail sloping down to the rear tip). Between the track frames sits a
// riveted khaki hull with a low cab, a roof silencer with exhaust stack, and a
// gun sponson protruding through each track frame. Faces +Z (forward = north),
// occupies a 1x2 tile lane.

const KHAKI = "#7a6f4e";
const GUNMETAL = "#4a4d52";
const TRACK = "#33342f";
const RUST = "#7a4a30";

function box(
  w: number, h: number, d: number, color: string,
  position: [number, number, number], rotation?: [number, number, number],
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation };
}

function cyl(
  r: number, h: number, seg: number, color: string,
  position: [number, number, number], rotation?: [number, number, number],
): Part {
  return { geometry: new THREE.CylinderGeometry(r, r, h, seg), color, position, rotation };
}

// The rhomboid profile in the z-y plane (z forward, y up), walked clockwise
// when seen from +x: front tip -> up over the front horn -> along the top ->
// down to the rear tip -> back along the ground run.
const PROFILE: ReadonlyArray<readonly [number, number]> = [
  [0.85, 0.22], // front tip (the raised nose horn)
  [0.45, 0.45], // crest of the front climb
  [-0.30, 0.40], // rear end of the top run
  [-0.85, 0.14], // rear tip, low
  [-0.45, 0.05], // ground run, rear
  [0.42, 0.05], // ground run, front
];

const TRACK_X = 0.30; // track centreline distance from the hull midline
const TRACK_W = 0.16; // track width across x
const TRACK_T = 0.10; // track link thickness (radial)

// One side's track ring: an angled box per profile edge, slightly overlong so
// the corners overlap into a closed lozenge.
function trackRing(x: number): Part[] {
  const out: Part[] = [];
  for (let i = 0; i < PROFILE.length; i++) {
    const [z0, y0] = PROFILE[i]!;
    const [z1, y1] = PROFILE[(i + 1) % PROFILE.length]!;
    const dz = z1 - z0;
    const dy = y1 - y0;
    const len = Math.hypot(dz, dy) + TRACK_T * 0.8;
    // Rotate about x so the box's long (local z) axis follows the edge.
    const rotX = Math.atan2(-dy, dz);
    out.push(box(TRACK_W, TRACK_T, len, TRACK, [x, (y0 + y1) / 2, (z0 + z1) / 2], [rotX, 0, 0]));
  }
  return out;
}

// A gun sponson protruding through one track frame, with a stubby six-pounder.
function sponson(side: 1 | -1): Part[] {
  const x = side * 0.34;
  return [
    box(0.24, 0.20, 0.34, KHAKI, [x, 0.24, 0.05]),
    // mantlet shield on the sponson's forward face
    box(0.16, 0.14, 0.04, GUNMETAL, [x + side * 0.02, 0.24, 0.235]),
    // the gun barrel, angled slightly outward
    {
      geometry: new THREE.CylinderGeometry(0.024, 0.03, 0.30, 6),
      color: GUNMETAL,
      position: [x + side * 0.05, 0.24, 0.36],
      rotation: [Math.PI / 2, side * 0.25, 0],
      eulerOrder: "YXZ",
    },
  ];
}

function parts(): Part[] {
  const out: Part[] = [
    ...trackRing(TRACK_X),
    ...trackRing(-TRACK_X),

    // hull between the track frames
    box(0.44, 0.30, 1.10, KHAKI, [0, 0.25, -0.05]),
    // angled bow plate reaching toward the front horn
    box(0.44, 0.05, 0.34, KHAKI, [0, 0.36, 0.62], [0.55, 0, 0]),
    // low driver's cab on the roof, between the top track runs
    box(0.28, 0.13, 0.32, KHAKI, [0, 0.455, 0.22]),
    // vision slit on the cab face
    box(0.20, 0.025, 0.02, "#22231f", [0, 0.475, 0.385]),

    // roof silencer and exhaust stack
    box(0.09, 0.07, 0.30, GUNMETAL, [0, 0.435, -0.22]),
    cyl(0.028, 0.16, 6, RUST, [0, 0.51, -0.32]),

    ...sponson(1),
    ...sponson(-1),
  ];

  // rivet studs along the hull bow and cab edges
  const rivets: Array<[number, number, number]> = [
    [-0.16, 0.34, 0.51], [0.16, 0.34, 0.51],
    [-0.16, 0.16, 0.51], [0.16, 0.16, 0.51],
    [-0.12, 0.52, 0.30], [0.12, 0.52, 0.30],
  ];
  for (const p of rivets) out.push(box(0.025, 0.025, 0.025, GUNMETAL, p));

  return out;
}

export const heavyTank = defineModel({
  id: "heavy-tank",
  name: "Heavy Tank",
  tags: ["vehicle", "wwi", "military", "tank", "armour", "low-poly"],
  batchId: "wwi",
  summary: "A rhomboid Great War landship with side sponson guns.",
  description:
    "A British Mark-style heavy tank: the classic lozenge silhouette, built " +
    "as a closed ring of angled track boxes per side, with a riveted khaki " +
    "hull, a low driver's cab, a roof silencer with exhaust stack and a " +
    "six-pounder sponson protruding through each track frame. Faces +Z and " +
    "fills a 1x2 tile lane.",
  author: "Fable 5",
  footprint: 0.9,
  tiles: { w: 1, h: 2 },
  parts,
  animations: [],
});
